import { IAPIMetadataItem } from 'library/ts/interfaces/metadata/api/IAPIMetadataItem';
import { MPXAvailableProfileType } from 'library/ts/model/enums/mpx/MPXAvailableProfileType';
import { ProjectInfo } from 'library/ts/ProjectInfo';
import { IDrmConfig } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/IDrmConfig';
import { IPTVResponse } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/IPTVResponse';
import { ContentCANVideoMediaType } from 'plugins/tag/plugin_data_service/ts/Application/model/enums/ContentCANVideoMediaType';
import { PTVContentType } from 'plugins/tag/plugin_data_service/ts/Application/model/enums/PTVContentType';
import { IContextMetadata } from 'tag/ts/tag/interfaces/metadata/IContextMetadata';
import { IParamsVO } from 'tag/ts/tag/interfaces/params/IParamsVO';
import { IParamsStitcherMetadataItem } from 'tag/ts/tag/interfaces/params/stitcher/IParamsStitcherMetadataItem';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';
import {
	CONTENT_CONFIG, STITCHER_CONFIG, STITCHER_DRM_TYPE, STREAM_TYPES
} from '../model/constants/PTVConstants';
import { PTVUrlBuilder } from './PTVUrlBuilder';

/**
 * @copyright © Paramount Streaming
 * @author Zeeshan Ahmad
 * @description PTV response builder utility for constructing enriched paramsVO responses
 */
export class PTVResponseBuilder {
	/**
	 * Builds enriched response for channel content
	 * @param {IParamsVO} paramsVO
	 * @param {string} assetURL
	 * @returns {IPTVResponse}
	 */
	static buildChannelResponse( paramsVO: IParamsVO, assetURL: string ): IPTVResponse {
		return this.#buildResponse( paramsVO, assetURL, {
			isLive: true,
			streamType: STREAM_TYPES.LIVE
		} );
	}

	/**
	 * Builds enriched response for movie or episode content
	 * @param {IParamsVO} paramsVO
	 * @param {string} assetURL
	 * @returns {IPTVResponse}
	 */
	static buildVodResponse( paramsVO: IParamsVO, assetURL: string ): IPTVResponse {
		return this.#buildResponse( paramsVO, assetURL, {
			isLive: false,
			streamType: STREAM_TYPES.VOD
		} );
	}

	/**
	 * @description Builds DRM configuration for Pluto TV Stitcher streams (Widevine, PlayReady, FairPlay).
	 *
	 * Format selection gates DASH (and therefore Widevine/PlayReady license requests) to
	 * compatible browsers via `PTVMetadataResolver.resolveStitchedPathFromWindow`. The
	 * FairPlay block is gated to Safari (`capabilities.isSafari`) AND non-STV embeds
	 * (`paramsVO.SYSTEM.embedType !== EmbedType.HTML5`). FairPlay is runtime-applicable
	 * only on Safari (Chrome/Firefox select Widevine, Edge legacy selects PlayReady),
	 * so emitting it elsewhere was redundant — and triggered avia-js DRM init to probe
	 * FP-related URLs, which fails under TLS-intercepting proxies (Proxyman, corporate
	 * Zscaler, etc.) and surfaces as HTML5_SRC_NOT_SUPPORTED (3101) on the manifest
	 * load. The `embedType` check additionally excludes STV/console embeds whose user
	 * agents masquerade as Safari (PS4/PS5, Xbox One, Samsung Tizen, LG webOS, Vizio,
	 * Comcast) — those are served by the HTML5 STV path with platform-specific DRM,
	 * not Safari FairPlay.
	 *
	 * On Safari, Avia runs its native HTML5 playback adapter (built-in HLS + EME).
	 * Blackbird's `SessionjsonPlugin` request hook reshapes the FairPlay license POST
	 * body to `{ kid, clipID, spc }` and appends `masterJWTPassthrough=true` to stitcher
	 * manifest requests so Stitcher embeds the JWT in playlist variant URIs (carrying
	 * auth through to key and fragment requests on Safari's native HLS path).
	 *
	 * @see https://paramount.atlassian.net/wiki/spaces/PLTCONTENT/pages/258409590/Service+Concierge
	 * @param {IParamsVO} paramsVO
	 * @returns {IDrmConfig}
	 * @private
	 */
	static #buildDrmConfig( paramsVO: IParamsVO ): IDrmConfig {
		const drmConfig: IDrmConfig = {
			widevine: {
				url: PTVUrlBuilder.buildWidevineUrl( paramsVO ),
				// Concierge requires the JWT as an Authorization: Bearer header on the license request.
				// avia-js ShakaAdapter.onRequest injects resource.location.drm.widevine.header into the XHR.
				header: {
					Authorization: `Bearer ${paramsVO.STITCHER.token}`
				}
			},
			playready: {
				url: PTVUrlBuilder.buildPlayReadyUrl( paramsVO )
			}
		};

		if (
			window.SmartTag?.capabilities?.isSafari
			&& paramsVO.SYSTEM.embedType !== EmbedType.HTML5
		) {
			drmConfig.fairplay = {
				appCertUrl: PTVUrlBuilder.buildFairPlayCertUrl( paramsVO ),
				licenseUrl: PTVUrlBuilder.buildFairPlayLicenseUrl( paramsVO ),
				header: {
					Authorization: `Bearer ${paramsVO.STITCHER.token}`
				}
			};
		}

		return drmConfig;
	}

	/**
	 * @description Assembles the full IPTVResponse: playback config (assetURL, DRM, adapter),
	 * Stitcher session config, and — when the site supplies STITCHER.channels/items/episodes —
	 * hydrated apiMetadata for Skin UI and CONTEXT.metadata for MOPS tracking.
	 * Both metadata buckets are omitted entirely when no source row is present.
	 * @param {IParamsVO} paramsVO
	 * @param {string} assetURL
	 * @param {object} options - isLive + streamType discriminators
	 * @returns {IPTVResponse}
	 * @private
	 */
	static #buildResponse(
		paramsVO: IParamsVO,
		assetURL: string,
		options: { isLive: boolean; streamType: string }
	): IPTVResponse {
		const apiMetadata = this.#buildApiMetadata( paramsVO, options.isLive );
		const contextMetadata = this.#buildContextMetadata( paramsVO, options.isLive, assetURL, apiMetadata );

		return {
			CONTENT: {
				assetURL,
				autoplay: CONTENT_CONFIG.autoplay,
				drm: this.#buildDrmConfig( paramsVO ),
				isLive: options.isLive,
				partner: CONTENT_CONFIG.partner,
				resource: {
					ad: {
						ssai: {}
					},
					playback: {
						adapter: { ID: CONTENT_CONFIG.adapterId }
					},
					metadata: contextMetadata ?? {}
				},
				...( apiMetadata ? { apiMetadata } : {} )
			},
			STITCHER: {
				stitcherVersion: STITCHER_CONFIG.version,
				token: paramsVO.STITCHER.token,
				adTechnology: STITCHER_CONFIG.adTechnology,
				// Honor consumer-supplied STITCHER.adBreakPreroll/Postroll when present, falling back to
				// the static default — lets the site drive preroll gating per-audience without a library
				// release. See PLBCK-6558. The server still gates supply via the JWT `vodPrerollAds` feature.
				adBreakPreroll: paramsVO.STITCHER.adBreakPreroll ?? STITCHER_CONFIG.adBreakPreroll,
				adBreakPostroll: paramsVO.STITCHER.adBreakPostroll ?? STITCHER_CONFIG.adBreakPostroll,
				method: STITCHER_CONFIG.method,
				streamType: options.streamType,
				inactivityTimeout: STITCHER_CONFIG.inactivityTimeout,
				minimumUpdateThreshold: STITCHER_CONFIG.minimumUpdateThreshold
			},
			...( contextMetadata ? { CONTEXT: { metadata: contextMetadata } } : {} )
		};
	}

	/**
	 * @description Builds the apiMetadata array from the channel or VOD item row supplied by the
	 * site (e.g. TN Web via STITCHER.channels[0] or STITCHER.items/episodes[0]).
	 *
	 * The full raw row is spread into the result so downstream consumers receive every field the
	 * site supplied — not just the IAPIMetadataItem-typed subset. For channels, normalized fields
	 * are layered on top to bridge Pluto naming (channelName vs title, currentListing[0] for the
	 * airing program) so Skin's existing reads resolve correctly. For VOD, TN Web's ContentCANVideo
	 * field names are 1:1 with IAPIMetadataItem (verified during PTVWS-3476), so the raw spread
	 * alone is sufficient — with a thumb → thumbnail bridge for Pluto's raw-endpoint shape.
	 *
	 * Spread order is `_apiRaw` → row → curated bridges. When a row carries a nested `_apiRaw` blob
	 * (the unmodified API object; see IParamsStitcherMetadataItem), it is spread first so fields the
	 * normalized shape omits top-level — e.g. `rating` + `regionalRatings[]`, which RatingManager
	 * gates on — surface in apiMetadata, while the row and bridges still win on naming/shape. The
	 * `_apiRaw` spread is a no-op when the field is absent. See PLBCK-7341.
	 *
	 * Returns undefined when no source row is present — caller omits apiMetadata from the response.
	 *
	 * @param {IParamsVO} paramsVO
	 * @param {boolean} isLive
	 * @returns {IAPIMetadataItem[] | undefined}
	 * @private
	 */
	static #buildApiMetadata( paramsVO: IParamsVO, isLive: boolean ): IAPIMetadataItem[] | undefined {
		if ( isLive ) {
			const channel = paramsVO.STITCHER?.channels?.[ 0 ];
			if ( !channel ) {
				return undefined;
			}

			// currentListing[0] is the currently-airing program — preferred source for title, rating, thumbnail.
			const listing = ( channel.currentListing as unknown[] )?.[ 0 ] as Record<string, unknown> | undefined;
			// Spread the raw API row first (when supplied) so buried fields surface for Skin; the full
			// channel row and the normalized IAPIMetadataItem bridges below override to win on naming/shape.
			// _apiRaw defaults to {} when absent, so behavior is unchanged on rows that don't carry it.
			const channelApiRaw: Record<string, unknown> = channel?._apiRaw ?? {};
			return [ {
				...channelApiRaw,
				...channel,
				title: ( listing?.title ?? channel.channelName ) as string,
				label: listing?.title as string,
				contentId: channel.id ?? channel._id,
				isLive: true,
				// Permanent Live/VLL kids surfacing; STCBE-1794 fixes the upstream listing value.
				isKidsContent: listing?.isKidsContent === true,
				thumbnail: ( listing?.resolvedfilePathThumb ?? channel?.resolvedfilePathLogo ?? channelApiRaw?.thumbnail ) as string,
				rating: ( listing?.rating ?? channelApiRaw?.rating ) as string,
				description: ( channel.description ?? listing?.description ) as string,
				seriesTitle: channel.channelName as string
				// as unknown as IAPIMetadataItem: the raw channel row carries fields outside the
				// IAPIMetadataItem shape; cast avoids importing every MPX enum to satisfy typing.
			} as unknown as IAPIMetadataItem ];
		}

		const item = paramsVO.STITCHER.type === PTVContentType.EPISODE
			? paramsVO.STITCHER.episodes?.[ 0 ]
			: paramsVO.STITCHER.items?.[ 0 ];
		if ( !item ) {
			return undefined;
		}

		// Most VOD fields land 1:1 with IAPIMetadataItem (title, label, contentId, duration, genre,
		// cbsShowId, seasonNum, episodeNum, seriesTitle, description) — verified during PTVWS-3476.
		// Exception: Pluto's raw endpoint uses `thumb` where Skin reads `thumbnail`
		// (TweenDownThumb, PINGate, EndPanel, StartPanel, ChromecastManager, MidCardPanel).
		// Bridge it explicitly so episode/movie thumbnails render regardless of whether the row
		// came from TN Web (hydrates `thumbnail`) or direct Pluto endpoint (`thumb`).
		//
		// _apiRaw (when supplied) carries the unmodified API row — rating + regionalRatings[] live
		// there on the Episode type (only regionalRating singular is exposed top-level), which gates
		// RatingManager off. Spread raw first so those surface; ...item and the thumb bridge still
		// win, and _apiRaw defaults to {} when absent so behavior is unchanged otherwise. PLBCK-7341.
		const itemApiRaw: Record<string, unknown> = item?._apiRaw ?? {};

		return [ {
			...itemApiRaw,
			...item,
			thumbnail: ( item?.thumbnail ?? item?.thumb ?? itemApiRaw?.thumbnail ?? itemApiRaw?.thumb ) as string
		} as unknown as IAPIMetadataItem ];
	}

	/**
	 * @description Builds context metadata for MOPS / Kochava tracking from the channel or VOD
	 * item row supplied by the site (TN Web via STITCHER.channels[0] or STITCHER.items/episodes[0]).
	 *
	 * Populates all `resource.metadata` fields defined in the Convergence Video Playback Tracking
	 * Spec that are derivable at `resourcestart` time from STITCHER data or player attributes.
	 *
	 * `ptvEpisodeId` is populated here from host-supplied STITCHER data — live readers read
	 * `associatedContentVideo.contentId` off the airing listing / current stream row; the VOD
	 * reader reads `item.contentId` (with `id` / `_id` fallbacks for legacy rows). It is NOT
	 * sourced from Blackbird's `SessionjsonPlugin`: that plugin fetches `session.json` after
	 * `CONTENT_START`, but `resource.metadata` is written once before the resource is handed
	 * to avia and is never re-read by the tracking pipeline, so a post-session-start value can
	 * never reach this object. Host-supplied STITCHER data is the only viable source at
	 * `resourcestart`.
	 *
	 * Fields NOT populated here (out of scope or no host source yet):
	 * - ptvClipId / ptvTimelineId / ptvChannelId / deltaFromClient — no STITCHER field maps to
	 *   these today. If they are required at `resourcestart`, the embedding app must supply them
	 *   via STITCHER (same pattern as `ptvEpisodeId`); the Blackbird `session.json` value cannot
	 *   reach `resource.metadata` for the reason above.
	 * - contPlayState / watchNextSourceType — end card / autoplay UX state; TN Web feature work.
	 * - mediaSdkVersion — tracking SDK version; not owned by Smart Tag.
	 *
	 * Alongside the curated fields, the unmodified upstream `_apiRaw` row (when TN Web supplies it
	 * per PTVWS-4933) is spread into the result *first* via `#resolveApiRawRow`, so raw fields the
	 * curated readers don't map top-level still reach `resource.metadata` and MOPS can map from
	 * source-of-truth at the Tealium config layer. Curated fields and derived DRM are layered on top
	 * and win on any key collision. No-op when `_apiRaw` is absent. PLBCK-7508.
	 *
	 * P+ and CBS.com paths are unaffected — this method only runs when STITCHER data is present
	 * (Pluto adapter path); P+ sites supply their own CONTEXT.metadata via the site layer.
	 *
	 * @see https://paramount.atlassian.net/browse/PLBCK-6473 — implementation ticket
	 * @see https://docs.google.com/document/d/1rUSn54u2FhsHlbORMw0Hhkx7nEc_W9bs9u6yJxSRjRo/edit?tab=t.q8d77su4zqkm#heading=h.tg8we0q21gsf — Convergence Video Playback Tracking Spec
	 * @param {IParamsVO} paramsVO
	 * @param {boolean} isLive
	 * @param {string} assetURL - Resolved manifest URL; exposed as mediaUrl in resource.metadata
	 * @param {IAPIMetadataItem[] | undefined} apiMetadata - Pre-built API metadata (merges _apiRaw → item); passed to the VOD reader so availableForProfileTypes resolves correctly for Pluto items that nest it inside _apiRaw
	 * @returns {Partial<IContextMetadata> | undefined}
	 * @private
	 */
	static #buildContextMetadata( paramsVO: IParamsVO, isLive: boolean, assetURL: string, apiMetadata: IAPIMetadataItem[] | undefined ): Partial<IContextMetadata> | undefined {
		const metadata = isLive
			? this.#buildLiveContextMetadata( paramsVO, assetURL )
			: this.#buildVodContextMetadata( paramsVO, assetURL, apiMetadata );

		// drm / drmType are derived (not host-supplied) and merged onto every platform reader's
		// output so the Conviva fields land in resource.metadata at resourcestart. PLBCK-6504.
		if ( !metadata ) {
			return metadata;
		}
		// Spread the unmodified upstream `_apiRaw` row first so fields the curated readers don't map
		// top-level still reach resource.metadata, letting MOPS map from source-of-truth at the
		// Tealium config layer. Curated tracking fields and derived DRM are layered on top to win on
		// naming/shape. No-op when `_apiRaw` is absent (all P+/CBS.com paths and pre-PTVWS-4933 Pluto
		// rows). Mirrors the #buildApiMetadata spread convention (PLBCK-7341). PLBCK-7508.
		return {
			...this.#resolveApiRawRow( paramsVO, isLive ),
			...metadata,
			...this.#deriveDrmMetadata( paramsVO, assetURL )
		} as Partial<IContextMetadata>;
	}

	/**
	 * @description Resolves the unmodified upstream `_apiRaw` blob for the currently-playing content,
	 * used to surface raw fields the curated `resource.metadata` shape doesn't map top-level so MOPS
	 * can map from source-of-truth at the Tealium config layer rather than from Smart Tag-derived flags.
	 *
	 * Reads `_apiRaw` off the same source row the context-metadata readers use: `channels[0]` for live,
	 * `episodes[0]` / `items[0]` for VOD (selected by `STITCHER.type`). Returns `{}` when no source row
	 * or no `_apiRaw` is present, so the spread in `#buildContextMetadata` is a no-op on rows that don't
	 * carry it (P+/CBS.com paths, pre-PTVWS-4933 Pluto rows). See IParamsStitcherMetadataItem.
	 *
	 * @param {IParamsVO} paramsVO
	 * @param {boolean} isLive
	 * @returns {Record<string, unknown>} The raw upstream API row, or `{}` when absent
	 * @private
	 */
	static #resolveApiRawRow( paramsVO: IParamsVO, isLive: boolean ): Record<string, unknown> {
		if ( isLive ) {
			return paramsVO.STITCHER?.channels?.[ 0 ]?._apiRaw ?? {};
		}
		const item = paramsVO.STITCHER.type === PTVContentType.EPISODE
			? paramsVO.STITCHER.episodes?.[ 0 ]
			: paramsVO.STITCHER.items?.[ 0 ];
		return item?._apiRaw ?? {};
	}

	/**
	 * @description Derives the Conviva DRM tracking fields (`drm`, `drmType`) for a Web Stitcher
	 * resource at `resourcestart`, before `attachResource`.
	 *
	 * Scope is Web only for now (desktop + mobile web), verified on desktop. CTV/STV
	 * (`EmbedType.HTML5`) embeds play protected streams via platform-specific DRM whose
	 * tracking mapping isn't yet confirmed, so they are left untouched here — a CTV discussion
	 * and possible implementation are planned as a follow-up (see TODO below) rather than risk
	 * mislabeling protected HLS as unprotected.
	 *
	 * For Web the active scheme is resolved from the selected stitched-path format and the
	 * runtime browser — the same signals `#buildDrmConfig` uses to decide which license config
	 * to emit. The streaming-url API only returns an MPD path once DRM is established for the
	 * session, so an `.mpd` `assetURL` is itself the protected signal (no browser sniffing);
	 * `.m3u8` plays clear except on Safari, where Avia's native HTML5 adapter uses FairPlay.
	 *
	 * Format is used deliberately over two alternatives: (1) the row's `isProtected` flag — a
	 * catalog value that can diverge from the stream actually served (observed: a title flagged
	 * `isProtected: false` that played DASH/Widevine), so the selected format is the truer
	 * signal; (2) concierge license-URL presence — not a discriminator, since `#buildDrmConfig`
	 * builds the Widevine/PlayReady URLs unconditionally, even for clear content.
	 *
	 * This must be derived pre-load: Conviva opens its session on `RESOURCE_START`, so the
	 * negotiated `DRM_KEY_SYSTEM_CREATED` value arrives too late to reach the tracking payload.
	 *
	 * @see https://paramount.atlassian.net/browse/PLBCK-6504 — Conviva drm / drmType requirement
	 * @param {IParamsVO} paramsVO
	 * @param {string} assetURL - Resolved manifest URL; `.mpd` (DASH) or `.m3u8` (HLS)
	 * @returns {Pick<IContextMetadata, 'drm' | 'drmType'>} drm fields, or `{}` for CTV/STV embeds
	 * @private
	 */
	static #deriveDrmMetadata( paramsVO: IParamsVO, assetURL: string ): Pick<IContextMetadata, 'drm' | 'drmType'> {
		// Web only — leave CTV/STV (HTML5 embeds) untouched pending a confirmed CTV DRM mapping.
		// TODO(PLBCK-6504): sync with CTV on how they determine the DRM flavor per device — i.e.
		// what signals (stitched-path format, concierge URL availability, platform key system)
		// map to drm/drmType for CTV — before extending this derivation to HTML5 embeds.
		if ( paramsVO.SYSTEM?.embedType === EmbedType.HTML5 ) {
			return {};
		}

		const isSafari = window.SmartTag?.capabilities?.isSafari === true;
		// Match on the manifest path only — strip the query/fragment first so a stray `.mpd`
		// or `.m3u8` in a query param can't mislabel the format (e.g. `.../master.m3u8?x=.mpd`).
		const manifestPath = assetURL?.split( /[?#]/ )[ 0 ] ?? '';
		let result: Pick<IContextMetadata, 'drm' | 'drmType'>;

		if ( manifestPath.endsWith( '.mpd' ) ) {
			// MPD is only served once the streaming-url API confirms DRM for the session.
			result = { drm: true, drmType: STITCHER_DRM_TYPE.WIDEVINE };
		} else if ( manifestPath.endsWith( '.m3u8' ) && isSafari ) {
			// Safari plays HLS via Avia's native adapter using FairPlay.
			result = { drm: true, drmType: STITCHER_DRM_TYPE.FAIRPLAY };
		} else {
			// Non-Apple HLS — AES ClearKey, i.e. no DRM (Daniel Baird, avia/Stitcher, 2026-06-04).
			// Tracked as `None` for Conviva since AES ClearKey is not a DRM scheme.
			result = { drm: false, drmType: STITCHER_DRM_TYPE.NONE };
		}

		return result;
	}

	/**
	 * @description Dispatches live context metadata construction to the platform-specific
	 * reader. SmartTag's live `STITCHER.channels[0]` arrives in two different shapes
	 * depending on which embedding app supplied it.
	 * The CTV gate uses the `EmbedType.HTML5` check to identify STV/CTV embeds — DESKTOP and MOBILE_WEB both take the web reader.
	 * @see https://paramount.atlassian.net/browse/PLBCK-6981
	 * @param {IParamsVO} paramsVO
	 * @param {string} assetURL
	 * @returns {Partial<IContextMetadata> | undefined}
	 * @private
	 */
	static #buildLiveContextMetadata( paramsVO: IParamsVO, assetURL: string ): Partial<IContextMetadata> | undefined {
		if ( paramsVO.SYSTEM?.embedType === EmbedType.HTML5 ) {
			return this.#buildCtvLiveContextMetadata( paramsVO, assetURL );
		}
		return this.#buildWebLiveContextMetadata( paramsVO, assetURL );
	}

	/**
	 * @description Builds context metadata for a live channel stream from
	 * `STITCHER.channels[0]` when the embedding app is TN Web. TN Web supplies the raw
	 * Pluto channels-API channel: program-level fields live under `currentListing[0]`
	 * (preferred), with channel-level fallbacks on the channel root.
	 * @param {IParamsVO} paramsVO
	 * @param {string} assetURL
	 * @returns {Partial<IContextMetadata> | undefined}
	 * @private
	 */
	static #buildWebLiveContextMetadata( paramsVO: IParamsVO, assetURL: string ): Partial<IContextMetadata> | undefined {
		const channel = paramsVO.STITCHER?.channels?.[ 0 ];
		if ( !channel ) {
			return undefined;
		}

		// currentListing[0] is the currently-airing program — preferred source for program-level fields.
		const listing = ( channel?.currentListing as unknown[] )?.[ 0 ] as Record<string, unknown> | undefined;
		// contentCANVideo carries genre, mediaType for the airing program.
		const contentCANVideo = listing?.contentCANVideo as Record<string, unknown> | undefined;
		const mediaType = ( contentCANVideo?.mediaType ) as string | undefined;
		const isMovie = listing?.entityType === 'movie';
		const isShow = listing?.entityType === 'show';

		return {
			videoId: ( channel?.id ?? channel?._id ) as string,
			videoTitle: ( listing?.title ?? channel?.channelName ) as string,
			isLive: true,
			streamType: STREAM_TYPES.LIVE,
			liveTvChannel: channel?.channelName as string,
			liveVideoTitle: listing?.title as string,
			channelId: ( channel?.id ?? channel?._id ) as string,
			channelName: channel?.channelName as string,
			channelSlug: ( channel?.slug ) as string,
			channelListingId: listing?.id !== undefined ? String( listing.id ) : undefined,
			categorySlug: ( channel?.channelCategorySlugs as string[] | undefined )?.[ 0 ],
			genre: ( contentCANVideo?.genre ) as string,
			mediaType: mediaType,
			mediaSdkVersion: ProjectInfo.release_version,
			mediaUrl: assetURL,
			isKidsContent: listing?.isKidsContent as boolean | undefined,
			duration: listing?.durationMins !== undefined ? ( listing.durationMins as number ) * 60 : undefined,
			ptvEpisodeId: ( listing?.associatedContentVideo as Record<string, unknown> | undefined )?.contentId as string,
			// CDMDIGMOPS-22483: ptvId is a listing-level identifier on channels.json currentListing[0].
			// TN Web must add ptvId to their currentListing GraphQL fields before this populates; until then it is undefined (no-op).
			ptvTimelineId: listing?.ptvId != null ? String( listing.ptvId ) : undefined,
			ptvChannelId: ( channel?.id ?? channel?._id ) as string,
			seriesTitle: undefined,
			// isEpisode: Clips and Trailers are not episodes.
			isEpisode: mediaType !== undefined ? ( mediaType !== ContentCANVideoMediaType.CLIP && mediaType !== ContentCANVideoMediaType.TRAILER ) : undefined,
			platform: paramsVO.SYSTEM?.platform,
			// Show-level fields are available directly on the listing row when a show episode is airing.
			// Gated on entityType === 'show' so one-off live events (sports, news with entityType: null)
			showSeriesId: isShow && listing?.showId ? ( listing?.showId as number ) : undefined,
			showSeriesTitle: isShow ? ( listing?.title as string ) : undefined,
			showSeasonNumber: isShow ? ( listing?.seasonNumber as string ) : undefined,
			showEpisodeId: isShow ? ( listing?.associatedContentVideo as Record<string, unknown> | undefined )?.contentId as string : undefined,
			showEpisodeTitle: isShow ? ( listing?.episodeTitle as string ) : undefined,
			showEpisodeNumber: isShow ? ( listing?.episodeNumber as string ) : undefined,
			// Gated on entityType === 'movie' so one-off live events (sports, news with entityType: null) don't emit.
			movieSeriesId: isMovie ? ( listing?.movieId as string ) : undefined,
			movieSeriesTitle: isMovie ? ( listing?.title as string ) : undefined,
			movieTitle: isMovie ? ( listing?.title as string ) : undefined,
			// CDMDIGMOPS-22483: movieId is the CAN/MDP content id from associatedContentVideo, not the channel's videoContentId.
			movieId: isMovie ? ( listing?.associatedContentVideo as Record<string, unknown> | undefined )?.contentId as string : undefined
		};
	}

	/**
	 * @description Builds context metadata for a live channel stream from
	 * `STITCHER.channels[0]` when the embedding app is a CTV (cbs-web-tv-apps).
	 *
	 * @see https://paramount.atlassian.net/browse/PLBCK-6981
	 * @param {IParamsVO} paramsVO
	 * @param {string} assetURL
	 * @returns {Partial<IContextMetadata> | undefined}
	 * @private
	 */
	static #buildCtvLiveContextMetadata( paramsVO: IParamsVO, assetURL: string ): Partial<IContextMetadata> | undefined {
		const currentStreamData = paramsVO.STITCHER?.channels?.[ 0 ];
		if ( !currentStreamData ) {
			return undefined;
		}

		const programContentId = currentStreamData?.contentId as string;
		const mediaType = currentStreamData?.mediaType as string | undefined;
		const isMovie = currentStreamData?.entityType === 'movie';
		const isShow = currentStreamData?.entityType === 'show';

		return {
			videoId: programContentId as string,
			videoTitle: currentStreamData?.title as string,
			isLive: true,
			streamType: STREAM_TYPES.LIVE,
			liveTvChannel: currentStreamData?.channelName as string,
			liveVideoTitle: currentStreamData?.title as string,
			channelId: currentStreamData?.channelId as string,
			channelName: currentStreamData?.channelName as string,
			channelSlug: currentStreamData?.channelSlug as string,
			channelListingId: currentStreamData?.channelListingId !== undefined ? String( currentStreamData?.channelListingId ) : undefined,
			categorySlug: ( currentStreamData?.categorySlug as string[] | undefined )?.[ 0 ],
			genre: currentStreamData?.genre as string,
			mediaType: mediaType,
			mediaSdkVersion: ProjectInfo.release_version,
			mediaUrl: assetURL,
			isKidsContent: currentStreamData?.isKidsContent as boolean | undefined,
			duration: currentStreamData?.durationMins !== undefined ? ( currentStreamData.durationMins as number ) * 60 : undefined,
			ptvEpisodeId: ( currentStreamData?.associatedContentVideo as Record<string, unknown> | undefined )?.contentId as string,
			seriesTitle: undefined,
			// isEpisode: Clips and Trailers are not episodes.
			isEpisode: mediaType !== undefined ? ( mediaType !== ContentCANVideoMediaType.CLIP && mediaType !== ContentCANVideoMediaType.TRAILER ) : undefined,
			platform: paramsVO.SYSTEM?.platform,
			// Show-level fields gated on entityType === 'show' so live one-offs (sports, news with
			// entityType: null) don't emit stale show identifiers — same rule as the web reader.
			showSeriesId: isShow && currentStreamData?.showId ? ( currentStreamData?.showId as number ) : undefined,
			showSeriesTitle: isShow ? ( currentStreamData?.title as string ) : undefined,
			showSeasonNumber: isShow ? ( currentStreamData?.seasonNumber as string ) : undefined,
			showEpisodeId: isShow ? programContentId : undefined,
			showEpisodeTitle: isShow ? ( currentStreamData?.episodeTitle as string ) : undefined,
			showEpisodeNumber: isShow ? ( currentStreamData?.episodeNumber as string ) : undefined,
			// PLBCK-7027 (52f6db4ef): empty string rather than duplicating movieId — Pluto's CMS has no movie-series concept.
			movieSeriesId: isMovie ? '' : undefined,
			movieSeriesTitle: isMovie ? ( currentStreamData?.title as string ) : undefined,
			movieTitle: isMovie ? ( currentStreamData?.title as string ) : undefined,
			movieId: isMovie ? programContentId : undefined
		};
	}

	/**
	 * @description Returns the first valid availableForProfileTypes array from normalized metadata or raw API metadata.
	 * @param {MPXAvailableProfileType[] | undefined} normalizedAvailableForProfileTypes
	 * @param {unknown} rawAvailableForProfileTypes
	 * @returns {MPXAvailableProfileType[] | undefined}
	 * @private
	 */
	static #resolveAvailableForProfileTypes(
		normalizedAvailableForProfileTypes: MPXAvailableProfileType[] | undefined,
		rawAvailableForProfileTypes: unknown
	): MPXAvailableProfileType[] | undefined {
		if ( Array.isArray( normalizedAvailableForProfileTypes ) ) {
			return normalizedAvailableForProfileTypes;
		}
		if ( Array.isArray( rawAvailableForProfileTypes ) ) {
			return rawAvailableForProfileTypes as MPXAvailableProfileType[];
		}
		return undefined;
	}

	/**
	 * @description Resolves the host-supplied, slug-shaped Pluto category slug for the content's
	 * own category, analogous to live `channelCategorySlugs[0]` (for example,
	 * `action-adventure`). Smart Tag validates only that the value is a non-empty scalar string;
	 * it does not derive or regex-validate the slug. A valid top-level value wins over the TN Web
	 * `_apiRaw` fallback.
	 * @see https://paramount.atlassian.net/browse/PLBCK-8139
	 * @see https://paramount.atlassian.net/browse/PTVWS-6118
	 * @param {IParamsStitcherMetadataItem} item
	 * @returns {string | undefined}
	 * @private
	 */
	static #resolveVodCategorySlug( item: IParamsStitcherMetadataItem ): string | undefined {
		const categorySlug = item.categorySlug;
		if ( typeof categorySlug === 'string' && categorySlug.length > 0 ) {
			return categorySlug;
		}

		const rawCategorySlug = item._apiRaw?.categorySlug;
		return typeof rawCategorySlug === 'string' && rawCategorySlug.length > 0
			? rawCategorySlug
			: undefined;
	}

	/**
	 * @description Builds context metadata for a VOD stream from STITCHER.items[0] or episodes[0].
	 * Gated on TN Web supplying the item row via PTVWS-3476.
	 * @param {IParamsVO} paramsVO
	 * @param {string} assetURL
	 * @param {IAPIMetadataItem[] | undefined} apiMetadata - Pre-built API metadata (merges _apiRaw → item); used to resolve availableForProfileTypes for Pluto items, which nest it inside _apiRaw rather than at the top level. PLBCK-7633.
	 * @returns {Partial<IContextMetadata> | undefined}
	 * @private
	 */
	static #buildVodContextMetadata( paramsVO: IParamsVO, assetURL: string, apiMetadata: IAPIMetadataItem[] | undefined ): Partial<IContextMetadata> | undefined {
		const item = paramsVO.STITCHER.type === PTVContentType.EPISODE
			? paramsVO.STITCHER.episodes?.[ 0 ]
			: paramsVO.STITCHER.items?.[ 0 ];
		if ( !item ) {
			return undefined;
		}

		// movieContent is the ContentCANVideo sub-object on movie rows from TN Web's useMovie hook.
		const movieContent = item.movieContent as Record<string, unknown> | undefined;
		const genre = ( movieContent?.genre ?? item.genre ) as string | undefined;
		const mediaType = item.mediaType as string | undefined;
		// Prefer the normalized field, but only when it is a real array. Some TN Web rows carry
		// `availableForProfileTypes: null`, which can shadow the valid `_apiRaw` value after merge.
		const normalizedAvailableForProfileTypes = apiMetadata?.[ 0 ]?.availableForProfileTypes;
		const rawAvailableForProfileTypes = item?._apiRaw?.availableForProfileTypes;
		const availableForProfileTypes = this.#resolveAvailableForProfileTypes(
			normalizedAvailableForProfileTypes,
			rawAvailableForProfileTypes
		);
		const isKidsContent = ( item.isKidsContent as boolean | undefined )
			?? ( availableForProfileTypes !== undefined
				? availableForProfileTypes.includes( MPXAvailableProfileType.KIDS )
					|| availableForProfileTypes.includes( MPXAvailableProfileType.YOUNGER_KIDS )
				: undefined );

		// STITCHER.type === EPISODE is the authoritative signal on converged Pluto stitcher rows
		// (which lack mediaType); mediaType is the fallback for P+ content paths where it is hydrated.
		// Clips and Trailers are not episodes.
		const resolveIsEpisode = (): boolean | undefined => {
			if ( paramsVO.STITCHER.type === PTVContentType.EPISODE ) {
				return true;
			}
			if ( mediaType === undefined ) {
				return undefined;
			}
			return mediaType !== ContentCANVideoMediaType.CLIP
				&& mediaType !== ContentCANVideoMediaType.TRAILER;
		};

		// Show-* / movie-* fields are mutually exclusive but both groups are always emitted. The same
		// contextMetadata object feeds CONTEXT.metadata and CONTENT.resource.metadata. On normal
		// subsequent API.VIDEO.load calls, VideoAPIUtilities.clearContentProperties() clears
		// CONTEXT.metadata but retains CONTENT.resource.
		// Explicit undefined keys let DeepMerge clear retained resource identifiers during direct merges.
		// PLBCK-7027: Pluto's CMS has no movie-series concept; emit '' rather than duplicate movieId.
		// Per Leslie Ding (MOPS) — Slack 2026-05-14: "set it to empty string or omit".
		const buildShowOrMovieFields = (): Partial<IContextMetadata> => {
			const isMovie = mediaType === ContentCANVideoMediaType.MOVIE;
			return isMovie ? {
				showSeriesId: undefined,
				showSeriesTitle: undefined,
				showSeasonNumber: undefined,
				showEpisodeId: undefined,
				showEpisodeTitle: undefined,
				showEpisodeNumber: undefined,
				movieSeriesId: '',
				movieSeriesTitle: item.title as string,
				movieId: ( movieContent?.contentId ?? item.contentId ) as string,
				movieTitle: item.title as string
			} : {
				showSeriesId: item.cbsShowId as number,
				showSeriesTitle: item.seriesTitle as string,
				showSeasonNumber: item.seasonNum as string,
				showEpisodeId: item.contentId as string,
				showEpisodeTitle: item.title as string,
				showEpisodeNumber: item.episodeNum as string,
				movieSeriesId: undefined,
				movieSeriesTitle: undefined,
				movieId: undefined,
				movieTitle: undefined
			};
		};

		return {
			// contentId priority: converged API uses 'contentId'; legacy / STITCHER.id uses 'id' / '_id'.
			videoId: ( item.contentId ?? item.id ?? item._id ) as string,
			videoTitle: item.title as string,
			isLive: false,
			streamType: STREAM_TYPES.VOD,
			mediaType: mediaType,
			seriesTitle: item.seriesTitle as string,
			genre: genre,
			isEpisode: resolveIsEpisode(),
			platform: paramsVO.SYSTEM?.platform,
			mediaSdkVersion: ProjectInfo.release_version,
			mediaUrl: assetURL,
			ptvEpisodeId: ( item.contentId ?? item.id ?? item._id ) as string,
			...buildShowOrMovieFields(),
			duration: item.duration as number | undefined,
			isKidsContent,
			// Live-only channel fields are explicitly emitted as undefined. Both metadata buckets use
			// this contextMetadata object. On normal subsequent API.VIDEO.load calls,
			// VideoAPIUtilities.clearContentProperties() clears CONTEXT.metadata but retains
			// CONTENT.resource. The explicit keys therefore clear prior resource values during
			// DeepMerge and direct/pre-lifecycle transformer invocation.
			// Mirrors the show/episode/movie clearing pattern above.
			channelId: undefined,
			channelName: undefined,
			channelSlug: undefined,
			channelListingId: undefined,
			liveTvChannel: undefined,
			liveVideoTitle: undefined,
			// VOD categorySlug is host-supplied only; an omitted or invalid value resolves to
			// undefined so DeepMerge clears category state retained from the prior resource.
			categorySlug: this.#resolveVodCategorySlug( item )
		};
	}
}